// src/pages/admin/AdminResidentRequests.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/Toast.jsx";
import "./admin.css";

export default function AdminResidentRequests() {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");
  const adminRole = localStorage.getItem("adminRole");

  const [activeTab, setActiveTab] = useState("pending"); // "pending", "approved", "rejected"
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API = process.env.REACT_APP_API_URL;

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
    }
  }, [token, navigate]);

  // Load residents based on active tab
  useEffect(() => {
    const loadResidents = async () => {
      try {
        setLoading(true);
        setError("");

        const endpoint =
          activeTab === "pending"
            ? "/api/admin/pending"
            : activeTab === "approved"
            ? "/api/admin/approved"
            : "/api/admin/rejected";

        const res = await fetch(`${API}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error("Failed to load residents");

        const data = await res.json();
        setResidents(Array.isArray(data) ? data : data.residents || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadResidents();
  }, [activeTab, API, token]);

  const handleApprove = async (residentId) => {
    try {
      const res = await fetch(`${API}/api/admin/approve/${residentId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to approve");

      setResidents((prev) => prev.filter((r) => r.id !== residentId));
      toast.success("Resident approved successfully");
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const handleReject = async (residentId) => {
    try {
      const res = await fetch(`${API}/api/admin/reject/${residentId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to reject");

      setResidents((prev) => prev.filter((r) => r.id !== residentId));
      toast.success("Resident rejected successfully");
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const getTabs = () => {
    // Count is based on current tab data
    const count = activeTab === "pending" ? residents.length : 0;
    return [
      { id: "pending", label: `⏳ Pending${activeTab === "pending" ? ` (${count})` : ""}` },
      { id: "approved", label: "✅ Approved" },
      { id: "rejected", label: "❌ Rejected" }
    ];
  };

  return (
    <div className="admin-dashboard">
      <h1>Resident Requests</h1>

      {/* Tabs */}
      <div className="resident-tabs">
        {getTabs().map((tab) => (
          <button
            key={tab.id}
            className={`resident-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading residents...</div>
      ) : residents.length === 0 ? (
        <div className="admin-empty">
          No {activeTab} requests found
        </div>
      ) : (
        <div className="residents-table-wrapper">
          <table className="residents-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Unit</th>
                <th>Tower</th>
                {adminRole === "admin" && <th>Type</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {residents.map((resident) => (
                <tr key={resident.id}>
                  <td>{resident.name}</td>
                  <td>{resident.email}</td>
                  <td>{resident.phone || "-"}</td>
                  <td>{resident.unitNumber || "-"}</td>
                  <td>{resident.tower}</td>
                  {adminRole === "admin" && (
                    <td>{resident.residentType || "-"}</td>
                  )}
                  <td className="action-cell">
                    {activeTab === "pending" && (
                      <>
                        <button
                          className="btn-approve"
                          onClick={() => handleApprove(resident.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => handleReject(resident.id)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {activeTab !== "pending" && <span>-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}